import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
  Tooltip,
  Badge,
  CircularProgress,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Schedule as ScheduleIcon,
  Notifications as NotificationsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  NotificationsActive as InAppIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import axios from 'axios';
import { API } from '../../API/api';
import { useAuth } from '../../Context/AuthContext';

const DocumentAlerts = () => {
  const { authState } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [dueReminders, setDueReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openReminderDialog, setOpenReminderDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [reminderForm, setReminderForm] = useState({
    days_before_expiry: 7,
    reminder_type: 'email',
    notification_channels: ['email'],
    custom_message: '',
    is_active: true,
  });

  const alertLevels = {
    critical: { color: 'error', icon: <ErrorIcon />, label: 'Critique' },
    warning: { color: 'warning', icon: <WarningIcon />, label: 'Attention' },
    info: { color: 'info', icon: <InfoIcon />, label: 'Information' },
    expired: { color: 'error', icon: <ErrorIcon />, label: 'Expiré' },
  };

  const reminderTypes = {
    email: 'Email',
    notification: 'Notification',
    sms: 'SMS',
    all: 'Tous les canaux',
  };

  const notificationChannels = {
    email: 'Email',
    sms: 'SMS',
    in_app: 'Dans l\'application',
  };

  useEffect(() => {
    fetchAlerts();
    fetchDueReminders();
  }, []);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/documents/alerts`, {
        headers: { Authorization: `Bearer ${authState.token}` },
      });
      setAlerts(response.data.data.documents || []);
    } catch (error) {
      console.error('Error fetching alerts:', error);
      setAlerts([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const fetchDueReminders = async () => {
    try {
      const response = await axios.get(`${API}/documents/due-reminders`, {
        headers: { Authorization: `Bearer ${authState.token}` },
      });
      setDueReminders(response.data.data.reminders || []);
    } catch (error) {
      console.error('Error fetching due reminders:', error);
      setDueReminders([]); // Set empty array on error
    }
  };

  const handleCreateReminder = (document) => {
    setSelectedDocument(document);
    setReminderForm({
      days_before_expiry: 7,
      reminder_type: 'email',
      notification_channels: ['email'],
      custom_message: `Rappel: Le document "${document.document_name}" expire bientôt.`,
      is_active: true,
    });
    setOpenReminderDialog(true);
  };

  const handleSaveReminder = async () => {
    try {
      await axios.post(`${API}/documents/${selectedDocument.id}/reminders`, reminderForm, {
        headers: { Authorization: `Bearer ${authState.token}` },
      });
      
      setOpenReminderDialog(false);
      fetchAlerts();
      fetchDueReminders();
    } catch (error) {
      console.error('Error creating reminder:', error);
    }
  };

  const handleMarkReminderAsSent = async (reminderId) => {
    try {
      await axios.post(`${API}/documents/reminders/${reminderId}/mark-sent`, {}, {
        headers: { Authorization: `Bearer ${authState.token}` },
      });
      fetchDueReminders();
    } catch (error) {
      console.error('Error marking reminder as sent:', error);
    }
  };

  const getAlertCount = (level) => {
    if (!Array.isArray(alerts)) {
      return 0;
    }
    return alerts.filter(alert => alert.alert_level === level).length;
  };

  const getDaysUntilExpiry = (expiryDate) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr} >
      <Box sx={{ p: 3 ,minHeight: '116vh' }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Alertes & Rappels
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Surveillez l'expiration de vos documents et gérez les rappels automatiques
          </Typography>
        </Box>

        {/* Alert Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}  >
            <Card sx={{ 
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb4b',
                borderRadius: '32px',
                padding: { xs: '20px', md: '30px' },
                marginBottom: '20px',
                boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)", 
            }} >
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Badge badgeContent={getAlertCount('critical')} color="error">
                    <ErrorIcon color="error" sx={{ mr: 2 }} />
                  </Badge>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Alertes Critiques
                    </Typography>
                    <Typography variant="h4">
                      {getAlertCount('critical')}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb4b',
                borderRadius: '32px',
                padding: { xs: '20px', md: '30px' },
                marginBottom: '20px',
                boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)", 
            }}>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Badge badgeContent={getAlertCount('warning')} color="warning">
                    <WarningIcon color="warning" sx={{ mr: 2 }} />
                  </Badge>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Alertes Attention
                    </Typography>
                    <Typography variant="h4">
                      {getAlertCount('warning')}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb4b',
                borderRadius: '32px',
                padding: { xs: '20px', md: '30px' },
                marginBottom: '20px',
                boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)", 
            }} >
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Badge badgeContent={getAlertCount('info')} color="info">
                    <InfoIcon color="info" sx={{ mr: 2 }} />
                  </Badge>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Informations
                    </Typography>
                    <Typography variant="h4">
                      {getAlertCount('info')}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb4b',
                borderRadius: '32px',
                padding: { xs: '20px', md: '30px' },
                marginBottom: '20px',
                boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)", 
            }} >
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Badge badgeContent={getAlertCount('expired')} color="error">
                    <ErrorIcon color="error" sx={{ mr: 2 }} />
                  </Badge>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Documents Expirés
                    </Typography>
                    <Typography variant="h4">
                      {getAlertCount('expired')}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* Active Alerts */}
          <Grid item xs={12} md={8}>
            <Card sx={{ 
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb4b',
                borderRadius: '32px',
                padding: { xs: '20px', md: '30px' },
                marginBottom: '20px',
                boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)", 
            }} >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Alertes Actives
                </Typography>
                <List>
                  {alerts.map((alert, index) => (
                    <React.Fragment key={alert.id}>
                      <ListItem>
                        <ListItemIcon>
                          {alertLevels[alert.alert_level]?.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="subtitle1">
                                {alert.document_name}
                              </Typography>
                              <Chip
                                label={alertLevels[alert.alert_level]?.label}
                                color={alertLevels[alert.alert_level]?.color}
                                size="small"
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {alert.related_entity_name} • 
                                Expire dans {getDaysUntilExpiry(alert.expiry_date)} jour(s)
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Expiration: {new Date(alert.expiry_date).toLocaleDateString('fr-FR')}
                              </Typography>
                            </Box>
                          }
                        />
                        <Box>
                          <Tooltip title="Créer un rappel">
                            <IconButton
                              size="small"
                              onClick={() => handleCreateReminder(alert)}
                            >
                              <AddIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListItem>
                      {index < alerts.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Due Reminders */}
          <Grid item xs={12} md={4}>
            <Card sx={{ 
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb4b',
                borderRadius: '32px',
                padding: { xs: '20px', md: '30px' },
                marginBottom: '20px',
                boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)", 
            }} >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Rappels à Envoyer
                </Typography>
                <List>
                  {dueReminders.map((reminder) => (
                    <ListItem key={reminder.id}>
                      <ListItemIcon>
                        <ScheduleIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={reminder.document.document_name}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Rappel dans {reminder.days_before_expiry} jour(s)
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Type: {reminderTypes[reminder.reminder_type]}
                            </Typography>
                          </Box>
                        }
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleMarkReminderAsSent(reminder.id)}
                      >
                        <CheckIcon />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Create Reminder Dialog */}
        <Dialog open={openReminderDialog} onClose={() => setOpenReminderDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Créer un Rappel pour "{selectedDocument?.document_name}"
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Jours avant expiration"
                  type="number"
                  value={reminderForm.days_before_expiry}
                  onChange={(e) => setReminderForm({ ...reminderForm, days_before_expiry: parseInt(e.target.value) })}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Type de rappel</InputLabel>
                  <Select
                    value={reminderForm.reminder_type}
                    onChange={(e) => setReminderForm({ ...reminderForm, reminder_type: e.target.value })}
                    label="Type de rappel"
                  >
                    {Object.entries(reminderTypes).map(([key, label]) => (
                      <MenuItem key={key} value={key}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Canaux de notification</InputLabel>
                  <Select
                    multiple
                    value={reminderForm.notification_channels}
                    onChange={(e) => setReminderForm({ ...reminderForm, notification_channels: e.target.value })}
                    label="Canaux de notification"
                  >
                    {Object.entries(notificationChannels).map(([key, label]) => (
                      <MenuItem key={key} value={key}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Message personnalisé"
                  value={reminderForm.custom_message}
                  onChange={(e) => setReminderForm({ ...reminderForm, custom_message: e.target.value })}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={reminderForm.is_active}
                      onChange={(e) => setReminderForm({ ...reminderForm, is_active: e.target.checked })}
                    />
                  }
                  label="Activer le rappel"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenReminderDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveReminder} variant="contained">
              Créer le rappel
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default DocumentAlerts;
